/**
 * Utility functions for player label rendering
 */

/**
 * Font stack for player labels - matches style guide §6
 */
export const PLAYER_LABEL_FONT_FAMILY = "'SF Compact Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

/** Label sizing constants - tuned for readability within player circles */
const LABEL_SIZING = {
	/** Available width as fraction of diameter (leaves padding to prevent clipping) */
	AVAILABLE_WIDTH_RATIO: 0.75,
	/** Single character fills this fraction of diameter (leaves room for stroke) */
	SINGLE_CHAR_RATIO: 0.55,
	/** Width multiplier for two-character labels */
	TWO_CHAR_WIDTH_MULT: 0.5,
	/** Width multiplier for 3+ character labels (more aggressive scaling) */
	MULTI_CHAR_WIDTH_MULT: 0.45,
	/** Minimum font size as fraction of radius (ensures readability) */
	MIN_SIZE_RATIO: 0.4,
	/** Maximum font size as fraction of diameter (prevents clipping) */
	MAX_SIZE_RATIO: 0.6,
} as const

/**
 * Calculate optimal font size to fill the player bubble based on label length.
 * Shorter labels get larger fonts to fill the space, longer labels scale down.
 *
 * @param label - The label text
 * @param radius - The circle radius
 * @param fontScale - Optional scaling factor (default 1.0)
 * @returns Calculated font size
 */
export function calculateLabelFontSize(label: string, radius: number, fontScale: number = 1.0): number {
	const diameter = radius * 2
	const availableWidth = diameter * LABEL_SIZING.AVAILABLE_WIDTH_RATIO

	const labelLength = label.length

	if (labelLength === 0) return radius * fontScale

	let fontSize: number
	if (labelLength === 1) {
		fontSize = diameter * LABEL_SIZING.SINGLE_CHAR_RATIO
	} else if (labelLength === 2) {
		fontSize = availableWidth / (labelLength * LABEL_SIZING.TWO_CHAR_WIDTH_MULT)
	} else {
		fontSize = availableWidth / (labelLength * LABEL_SIZING.MULTI_CHAR_WIDTH_MULT)
	}

	const minFontSize = radius * LABEL_SIZING.MIN_SIZE_RATIO
	const maxFontSize = diameter * LABEL_SIZING.MAX_SIZE_RATIO

	return Math.max(minFontSize, Math.min(maxFontSize, fontSize * fontScale))
}
